import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import icon from "../../assets/icon.svg";
import icon1 from "../../assets/icon1.svg";
import squareMeters from "../../assets/SquareMeters.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { CollectionPickerModal } from "./CollectionPickerModal";
import CollectionCreateModal from "./CollectionCreateModal";
import { CurrencySymbol } from "../../utils/enums";

export default function CollectionPropertyCard({
  property,
  isFavorite = false,
  isAuthenticate,
  setAuthPopUp,
  initialSelectedCollectionIds = [],
  onCreateNewCollection,
}) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const p = property || {};
  const images = p?.frontPageImg || [];
  const mainImage = images[0];

  const handleCardClick = () => {
    navigate(`/property-house/${p?._id || p?.id}`);
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    if (!isAuthenticate && setAuthPopUp) {
      setAuthPopUp(true);
      return;
    }
    setPickerOpen(true);
  };

  const priceText =
    p?.price && p?.price_display_checked === "show_actual_price"
      ? `${CurrencySymbol.NGN} ` + String(p.price).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : p?.price_display || null;

  const propertyTitle =
    p?.listingTitle ||
    p?.title ||
    p?.street_address_name ||
    `${p?.street_address_number || ""} ${p?.street_address_name || ""}`.trim() ||
    "Property";

  const fullAddress = [
    p?.street_address_number,
    p?.street_address_name,
    p?.suburb,
    p?.municipality,
  ]
    .filter(Boolean)
    .join(", ");

  const areaText = p?.type?.startsWith("House") ? p?.houseSize : p?.land_size;

  const primaryColor = p?.primary_color || p?.lead_agent?.agency_id?.primary_color;
  const secondaryColor = p?.secondary_color || p?.lead_agent?.agency_id?.secondary_color;
  const textColor = p?.text_color || p?.lead_agent?.agency_id?.text_color;

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border-[1.5px] border-[#EAEAEA] transition-all duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* 🔹 Property Image */}
      <div className="relative w-full">
        {/* Main Image */}
        {mainImage ? (
          <img
            src={mainImage}
            alt="property"
            className="w-full h-56 object-cover"
          />
        ) : (
          <div className="w-full h-56 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
            No image
          </div>
        )}

        {/* 50px Top Header Overlay */}
        <div
          className={`absolute top-0 left-0 w-full h-12 flex items-center px-4 py-1 ${primaryColor ? "" : "bg-black"}`}
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        >
          <div className="flex items-center min-w-0">
            {p?.lead_agent?.agencyLogo || p?.agency_id?.agencyMediumLogo ? (
              <LazyLoadImage
                src={p?.lead_agent?.agencyLogo || p?.agency_id?.agencyMediumLogo}
                alt=""
                className="h-8 w-auto py-1 object-contain"
                effect="blur"
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">
                {p?.lead_agent?.agencyName || p?.lead_agent?.name || "Agency"}
              </span>
            )}
          </div>
        </div>

        {/* Profile Logo (Overlapping Header + Image) */}
        {p?.lead_agent?.profileImg && (
          <div className="absolute right-2 top-[18px]">
            <LazyLoadImage
              src={p.lead_agent.profileImg}
              alt=""
              className="h-[58px] w-[58px] rounded-full object-cover border-[3.5px] border-[#E9E9E9]"
              effect="blur"
            />
          </div>
        )}
      </div>

      {/* 🔹 Card Body */}
      <div className="p-4 flex flex-col ">
        <div className="flex items-center justify-between ">
          <div className="flex flex-col gap-1">
            {/* Price */}
            <h3 className="text-blue-600 text-xl font-bold">{priceText}</h3>

            {/* Title */}
            <h2 className="text-lg font-semibold text-gray-800 mt-1 truncate w-36 xl:w-60">
              {propertyTitle}
            </h2>
          </div>
          {/* Heart Icon */}
          <div onClick={handleFavorite}>
            {isFavorite ? (
              <FaHeart color="#097FE6" size={20} />
            ) : (
              <FiHeart color="#097FE6" size={20} />
            )}
          </div>
        </div>
        {/* Address */}
        <div className="flex items-center gap-2 text-gray-500 text-sm mt-3">
          <p className="line-clamp-1">{fullAddress}</p>
        </div>
        <div>
          {/* Divider */}
          <div className="border-t my-3"></div>

          {/* Features */}
          <div className="flex items-center justify-between xl:gap-3 gap-0 text-[#000929] text-xs font-medium">
            {p?.Bathrooms != null && (
              <span className="flex items-center gap-2">
                <img src={icon1} alt="icon" className="w-4 h-4 shrink-0" />
                {p.Bathrooms} Beds
              </span>
            )}
            {p?.Bedrooms != null && (
              <span className="flex items-center gap-2">
                <img src={icon} alt="icon" className="w-4 h-4 shrink-0" />
                {p.Bedrooms} Bathrooms
              </span>
            )}
            {areaText && (
              <span className="flex items-center gap-2">
                <img
                  src={squareMeters}
                  alt="icon"
                  className="w-4 h-4 shrink-0"
                />
                {areaText} m²
              </span>
            )}
          </div>
        </div>
      </div>

      <CollectionPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        propertyId={p?.id || p?._id}
        onCreateNewCollection={() => setCreateModalOpen(true)}
        initialSelectedIds={
          p?.collections?.map((col) => col.id ?? col._id ?? col.collectionId) ||
          []
        }

      />

      {createModalOpen && (
        <CollectionCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          editingCollection={null}
          onSaved={() => { }}
        />
      )}
    </div>
  );
}
